import React, { useState, useEffect } from 'react';
import { getRuntimeConfig } from '../config/runtimeConfig';
import {
  makeStyles,
  shorthands,
  Text,
  Card,
  CardHeader,
  Button,
  Input,
  Field,
  tokens,
  Badge,
  Spinner,
  Switch,
  CounterBadge,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  mergeClasses,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody, MessageBarActions } from '../components/Common/ScrollableMessageBar';
import {
  CloudSync24Regular,
  Save24Regular,
  Key24Regular,
  Link24Regular,
  Checkmark24Regular,
  Warning24Regular,
  ArrowClockwise24Regular,
  ArrowSync24Regular,
  Copy24Regular,
  Alert24Regular,
  DismissRegular,
  Dismiss24Regular,
  Delete20Regular,
  Add20Regular,
  ArrowLeftRegular,
  KeyReset24Regular,
  ErrorCircle20Filled } from
'@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/apiClient';
import { ServiceError } from '../services/ServiceError';
import { useUnifiedApp } from '../contexts/UnifiedAppContext';
import { getDifferenceInDays } from '../utils/dateUtils';
import useEventTracker from '../hooks/useEventTracker';
import { ElementButton, PageName } from '../tracking/tracking.types';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalXL,
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
    ...shorthands.borderBottom('3px', 'solid', '#0047AB'),
    ...shorthands.padding('0', '0', '20px', '0'),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a1a1a',
    letterSpacing: '-0.5px',
    marginBottom: tokens.spacingVerticalS
  },
  subtitle: {
    fontSize: '16px',
    color: tokens.colorNeutralForeground2
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: tokens.spacingHorizontalL
  },
  card: {
    marginBottom: tokens.spacingVerticalL,
    ...shorthands.padding('24px')
  },
  statusCard: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding(tokens.spacingVerticalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    marginBottom: tokens.spacingVerticalL
  },
  statusGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalM
  },
  statusItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius(tokens.borderRadiusSmall)
  },
  fieldGroup: {
    marginBottom: '20px'
  },
  fieldGroupContent: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '10px'
  },
  fieldGroupSubContent: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '4px'
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL
  },
  codeBlock: {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('6px'),
    fontFamily: 'monospace',
    fontSize: '13px',
    marginTop: '8px',
    wordBreak: 'break-all'
  },
  testSection: {
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground1
  },
  restrictedDomainsSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
    ...shorthands.padding('20px'),
    backgroundColor: '#f8f8f8',
    ...shorthands.borderRadius('8px')
  },
  domainsList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    maxHeight: '200px',
    overflowY: 'auto'
  },
  domainItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('8px', '12px'),
    backgroundColor: '#ffffff',
    ...shorthands.border('1px', 'solid', '#e8e8e8'),
    ...shorthands.borderRadius('4px')
  },
  domainInput: {
    display: 'flex',
    ...shorthands.gap('8px'),
    marginTop: '8px'
  },
  warningText: {
    fontSize: '13px',
    color: '#a4262c',
    backgroundColor: '#fdf2f2',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius('4px')
  },
  removeButton: {
    minWidth: '24px',
    height: '24px',
    color: '#a4262c'
  },
  hidden: {
    display: 'none'
  },
  hiddenForPilot: {
    display: 'none'
  },
  subtitleText: {
    fontSize: '12px'
  },
  apiKeyActions: {
    marginTop: '12px'
  },
  inlineIconText: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS
  },
  noPadding: {
    padding: 0
  },
  noMarginBottom: {
    marginBottom: 0
  }
});

interface SyncResponse {
  success?: boolean;
  added?: number;
  updated?: number;
  removed?: number;
  totalDomains?: number;
  syncedCount?: number;
  error?: string;
}

interface DomainFeedConfig {
  apiUrl: string;
  apiKey: string;
  enabled: boolean;
  syncIntervalMinutes: number;
  lastSync?: string;
  nextSync?: string;
  lastSyncStatus?: 'success' | 'failed';
  lastSyncMessage?: string;
  totalDomains?: number;
  totalDomainsSynced?: number;
  approvedDomains?: number;
  pendingDomains?: number;
  autoApproveDomains?: boolean;
  restrictedDomains?: string[];
  allowPolicyUpdates?: boolean;
  emailNotifications?: {
    enabled: boolean;
    recipientEmail: string;
    recipientEmails?: string[];
    notifyOnNewDomains: boolean;
    notifyOnRemovedDomains: boolean;
    notifyOnApprovalRequired: boolean;
  };
}

interface ConnectionStatus {
  connected: boolean;
  lastCheck?: string;
  error?: string;
  domainCount?: number;
  lastUpdated?: string;
}

interface ConfigResponse {
  apiUrl?: string;
  apiKey?: string;
  enabled?: boolean;
  syncIntervalMinutes?: number;
  lastSync?: string;
  totalDomains?: number;
  approvedDomains?: number;
  pendingDomains?: number;
  restrictedDomains?: string[];
  emailNotifications?: {
    enabled: boolean;
    recipientEmail: string;
    recipientEmails?: string[];
    notifyOnNewDomains: boolean;
    notifyOnRemovedDomains: boolean;
    notifyOnApprovalRequired: boolean;
  };
}

interface TestConnectionResponse {
  success?: boolean;
  error?: string;
  domainCount?: number;
  lastUpdated?: string;
}

interface SyncResponse {
  success?: boolean;
  error?: string;
  data?: {
    domainsProcessed: number;
    domainsAdded: number;
    domainsUpdated: number;
    domainsRemoved: number;
    message: string;
    hasChanges: boolean;
    approvedDomainsToRemove: any[];
    pendingApprovals: any[];
    errors: any[];
  };
}

const DEFAULT_DOMAIN_FEED_URL = 'https://app-gateway-wsteams.workspace.lseg.com/od-service/v1/domains';

const getDefaultDomainFeedUrl = (): string => {
  try {
    return getRuntimeConfig().domainFeedUrl || DEFAULT_DOMAIN_FEED_URL;
  } catch {
    return DEFAULT_DOMAIN_FEED_URL;
  }
};

const isValidDomainFormat = (domain: string): boolean => {
  if (!domain.trim()) return false;
  const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  return domainRegex.test(domain.trim());
};

const getApiKeyClaims = (apiKey: string): any => {
  try {
    const payload = apiKey.split('.')[1];
    const decodedPayload = atob(payload);
    return JSON.parse(decodedPayload);
  } catch {
    return null;
  }
};

const getApiKeyExpirationDate = (apiKey: string): Date | null => {
  const claims = getApiKeyClaims(apiKey);
  if (claims?.exp) {
    return new Date(claims.exp * 1000);
  }
  return null;
};

type DomainFeedSettingsStyles = ReturnType<typeof useStyles>;

type SettingsMessage = {
  type: 'success' | 'error' | 'info';
  text: string;
};

const getMessageIntent = (type: SettingsMessage['type']): 'success' | 'info' | 'error' => {
  if (type === 'info' || type === 'error') {
    return type;
  }
  return 'success';
};

const getAddDomainButtonTitle = (isDomainDuplicate: boolean, isInvalidDomain: boolean): string => {
  if (isDomainDuplicate) {
    return 'This domain is already in the restricted list';
  }

  if (isInvalidDomain) {
    return 'Invalid domain format';
  }

  return '';
};

const TokenExpiryText: React.FC<{apiKeyExpirationDate: Date | null;styles: DomainFeedSettingsStyles;}> = ({
  apiKeyExpirationDate,
  styles
}) => {
  if (apiKeyExpirationDate) {
    const daysRemaining = getDifferenceInDays(new Date(), apiKeyExpirationDate);
    if (daysRemaining >= 0) {
      const daySuffix = daysRemaining === 1 ? '' : 's';
      return (
        <Text className={styles.subtitleText} data-testid="domain-feed-settings-token-expiry-value-text">{`${apiKeyExpirationDate.toDateString().replace(/^\S+\s/, '')} (Expires in ${daysRemaining} day${daySuffix})`}</Text>);

    }
    return (
      <div className={mergeClasses(styles.inlineIconText, styles.warningText)} data-testid="domain-feed-settings-inline-icon-text-div">
        <ErrorCircle20Filled data-testid="domain-feed-settings-error-circle20-filled" />
        <Text className={mergeClasses(styles.warningText, styles.noPadding)} data-testid="domain-feed-settings-your-token-expired-in-text">Your token expired in <b data-testid="domain-feed-settings-b">{apiKeyExpirationDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</b>. Select <b data-testid="domain-feed-settings-b-2-b">Generate new API key</b> to continue.</Text>
      </div>);

  }
  return (
    <Text className={styles.subtitleText} data-testid="domain-feed-settings-n-a-text">N/A</Text>);

};

const getNextSyncDisplay = (config: DomainFeedConfig): string => {
  if (config.nextSync) {
    return new Date(config.nextSync).toLocaleString();
  }
  if (config.lastSync) {
    return new Date(new Date(config.lastSync).getTime() + config.syncIntervalMinutes * 60000).toLocaleString();
  }
  return 'After first sync';
};

interface MessageBannerProps {
  message: SettingsMessage | null;
  onDismiss: () => void;
}

const MessageBanner: React.FC<MessageBannerProps> = ({ message, onDismiss }) => {
  if (!message) {
    return null;
  }

  return (
    <MessageBar
      intent={getMessageIntent(message.type)}
      style={{ marginBottom: '20px' }} data-testid="domain-feed-settings-message-bar">

      <MessageBarBody data-testid="domain-feed-settings-message-bar-body">{message.text}</MessageBarBody>
      <MessageBarActions
      containerAction={
      <Button
        onClick={onDismiss}
        aria-label="dismiss"
        appearance="transparent"
        icon={<DismissRegular data-testid="domain-feed-settings-dismiss-regular" />} data-testid="domain-feed-settings-dismiss-button" />

      } data-testid="domain-feed-settings-message-bar-actions" />

    </MessageBar>);

};

interface NotificationsCardProps {
  styles: DomainFeedSettingsStyles;
  notifications: any[];
  onAcknowledge: (id: number) => void;
}

const NotificationsCard: React.FC<NotificationsCardProps> = ({ styles, notifications, onAcknowledge }) => {
  if (!notifications.length) {
    return null;
  }

  return (
    <Card className={styles.statusCard} style={{ marginBottom: '20px' }} data-testid="domain-feed-settings-notifications-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }} data-testid="domain-feed-settings-notifications-header-div">
        <Alert24Regular style={{ color: tokens.colorPaletteYellowForeground1 }} data-testid="domain-feed-settings-alert24-regular" />
        <Text size={400} weight="semibold" data-testid="domain-feed-settings-domain-feed-changes-detected-text">Domain Feed Changes Detected</Text>
        <CounterBadge count={notifications.length} color="important" data-testid="domain-feed-settings-counter-badge" />
      </div>
      {notifications.map((notification) =>
      <div key={notification.id} style={{
        padding: '12px',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: '6px',
        marginBottom: '8px',
        border: `1px solid ${tokens.colorNeutralStroke1}`
      }} data-testid="domain-feed-settings-notification-card-div">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }} data-testid="domain-feed-settings-notification-layout-div">
            <div style={{ flex: 1 }} data-testid="domain-feed-settings-notification-details-div">
              {notification.total_new > 0 &&
              <div style={{ marginBottom: '8px' }} data-testid="domain-feed-settings-notification-new-domains-div">
                  <Badge appearance="filled" color="success" style={{ marginRight: '8px' }} data-testid="domain-feed-settings-notification-new-badge">
                    +{notification.total_new} New
                  </Badge>
                  <Text size={200} data-testid="domain-feed-settings-new-domains-added-text">
                    New domains added: {notification.new_domains?.slice(0, 3).join(', ')}
                    {notification.new_domains?.length > 3 && ` and ${notification.new_domains.length - 3} more`}
                  </Text>
                </div>
              }
              {notification.total_removed > 0 &&
              <div data-testid="domain-feed-settings-notification-removed-domains-div">
                  <Badge appearance="filled" color="danger" style={{ marginRight: '8px' }} data-testid="domain-feed-settings-notification-removed-badge">
                    -{notification.total_removed} Removed
                  </Badge>
                  <Text size={200} data-testid="domain-feed-settings-domains-removed-text">
                    Domains removed: {notification.removed_domains?.slice(0, 3).join(', ')}
                    {notification.removed_domains?.length > 3 && ` and ${notification.removed_domains.length - 3} more`}
                  </Text>
                </div>
              }
              <div style={{ marginTop: '8px' }} data-testid="domain-feed-settings-notification-timestamp-div">
                <Text size={100} style={{ color: tokens.colorNeutralForeground3 }} data-testid="domain-feed-settings-notification-timestamp-text">
                  {new Date(notification.created_at).toLocaleString()}
                </Text>
              </div>
            </div>
            <Button
            appearance="subtle"
            icon={<Dismiss24Regular data-testid="domain-feed-settings-notification-dismiss24-regular" />}
            onClick={() => onAcknowledge(notification.id)}
            title="Acknowledge and dismiss" data-testid="domain-feed-settings-acknowledge-and-dismiss-button" />

          </div>
        </div>
      )}
    </Card>);

};

interface ConnectionStatusCardProps {
  styles: DomainFeedSettingsStyles;
  connectionStatus: ConnectionStatus;
  config: DomainFeedConfig;
  syncing: boolean;
  onSyncDomains: () => void;
}

const ConnectionStatusCard: React.FC<ConnectionStatusCardProps> = ({
  styles,
  connectionStatus,
  config,
  syncing,
  onSyncDomains
}) => {
  const lastSyncDate = config.lastSync ? new Date(config.lastSync) : null;

  return (
    <Card className={styles.statusCard} data-testid="domain-feed-settings-connection-status-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} data-testid="domain-feed-settings-connection-header-div">
        <div data-testid="domain-feed-settings-connection-status-summary-div">
          <Text size={400} weight="semibold" data-testid="domain-feed-settings-connection-status-text">Connection Status</Text>
          <div style={{ marginTop: '8px' }} data-testid="domain-feed-settings-connection-status-badges-div">
            {connectionStatus.connected ?
            <Badge appearance="filled" color="success" data-testid="domain-feed-settings-connected-badge">
                <Checkmark24Regular style={{ fontSize: '12px' }} data-testid="domain-feed-settings-checkmark24-regular" /> Connected
              </Badge> :

            <Badge appearance="filled" color="danger" data-testid="domain-feed-settings-disconnected-badge">
                <Warning24Regular style={{ fontSize: '12px' }} data-testid="domain-feed-settings-warning24-regular" /> Disconnected
              </Badge>
            }
            {connectionStatus.lastCheck &&
            <Text size={100} style={{ marginLeft: '12px', color: tokens.colorNeutralForeground3 }} data-testid="domain-feed-settings-last-checked-text">
                Last checked: {new Date(connectionStatus.lastCheck).toLocaleString()}
              </Text>
            }
          </div>
        </div>
        {connectionStatus.connected &&
        <Button
          appearance="primary"
          icon={<ArrowSync24Regular data-testid="domain-feed-settings-arrow-sync24-regular" />}
          onClick={onSyncDomains}
          disabled={syncing || !config.apiKey} data-testid="domain-feed-settings-sync-domains-button">

            {syncing ? 'Syncing...' : 'Sync Domains Now'}
          </Button>
        }
      </div>

      {connectionStatus.connected &&
      <div className={styles.statusGrid} data-testid="domain-feed-settings-status-grid-div">
          <div className={styles.statusItem} data-testid="domain-feed-settings-status-item-div">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} data-testid="domain-feed-settings-domain-count-metrics-div">
              <Text size={500} weight="semibold" data-testid="domain-feed-settings-total-domains-value-text">{config.totalDomainsSynced || config.totalDomains || 0}</Text>
              <Text size={200} data-testid="domain-feed-settings-total-domains-text">Total Domains</Text>
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }} data-testid="domain-feed-settings-domain-count-badges-div">
                <Badge appearance="filled" color="success" size="small" data-testid="domain-feed-settings-approved-badge">
                  {config.approvedDomains || 0} Approved
                </Badge>
                <Badge appearance="filled" color="warning" size="small" data-testid="domain-feed-settings-pending-badge">
                  {config.pendingDomains || 0} Pending
                </Badge>
              </div>
            </div>
          </div>
          <div className={styles.statusItem} data-testid="domain-feed-settings-status-item-2-div">
            <Text size={500} weight="semibold" data-testid="domain-feed-settings-feed-domains-value-text">{connectionStatus.domainCount || 0}</Text>
            <Text size={200} data-testid="domain-feed-settings-domains-in-lseg-feed-text">Domains in LSEG Feed</Text>
          </div>
          <div className={styles.statusItem} data-testid="domain-feed-settings-status-item-3-div">
            <Text size={500} weight="semibold" style={{ textAlign: 'center' }} data-testid="domain-feed-settings-last-sync-value-text">
              {lastSyncDate ?
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }} data-testid="domain-feed-settings-last-sync-date-time-div">
                  <div data-testid="domain-feed-settings-last-sync-date-div">{lastSyncDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}</div>
                  <div style={{ fontSize: '14px', fontWeight: 'normal' }} data-testid="domain-feed-settings-last-sync-time-div">
                    {lastSyncDate.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                  </div>
                </div> :
              'Never'}
            </Text>
            <Text size={200} data-testid="domain-feed-settings-last-sync-label-text">Last Sync</Text>
          </div>
        </div>
      }

      {!connectionStatus.connected && connectionStatus.error &&
      <div style={{ marginTop: '12px', padding: '12px', backgroundColor: tokens.colorPaletteRedBackground1, borderRadius: '4px', border: `1px solid ${tokens.colorPaletteRedBorder1}` }} data-testid="domain-feed-settings-connection-error-div">
          <Text size={300} weight="semibold" style={{ color: tokens.colorPaletteRedForeground1 }} data-testid="domain-feed-settings-connection-error-text">
            Connection Error
          </Text>
          <Text size={200} style={{ marginTop: '4px', display: 'block' }} data-testid="domain-feed-settings-connection-error-message-text">
            {connectionStatus.error}
          </Text>
        </div>
      }
    </Card>);

};

interface ApiConfigurationCardProps {
  styles: DomainFeedSettingsStyles;
  config: DomainFeedConfig;
  showApiKey: boolean;
  apiKeyExpirationDate: Date | null;
  generatingApiKey: boolean;
  generateApiKeyDialog: boolean;
  onConfigChange: (key: keyof DomainFeedConfig, value: any) => void;
  onToggleApiKeyVisibility: () => void;
  onCopyApiKey: () => void;
  onGenerateApiKeyDialogChange: (open: boolean) => void;
  onGenerateNewApiKey: () => void;
  shouldShowGenerateApiKeyDialog: () => boolean;
}

const ApiConfigurationCard: React.FC<ApiConfigurationCardProps> = ({
  styles,
  config,
  showApiKey,
  apiKeyExpirationDate,
  generatingApiKey,
  generateApiKeyDialog,
  onConfigChange,
  onToggleApiKeyVisibility,
  onCopyApiKey,
  onGenerateApiKeyDialogChange,
  onGenerateNewApiKey,
  shouldShowGenerateApiKeyDialog
}) => {
  const domainFeedPlaceholder = getDefaultDomainFeedUrl();

  return (
    <Card className={styles.card} data-testid="domain-feed-settings-api-configuration-card">
      <CardHeader
        header={<Text weight="semibold" data-testid="domain-feed-settings-api-configuration-text">API Configuration</Text>}
        description="Configure API endpoint and authentication settings." data-testid="domain-feed-settings-api-configuration-card-header" />

      <div className={mergeClasses(styles.fieldGroup, styles.hidden)} data-testid="domain-feed-settings-field-group-div">
        <Field
          label="API Endpoint URL"
          hint="API endpoint URL provided by the domain feed provider"
          required data-testid="domain-feed-settings-api-endpoint-url-field">

          <Input
            value={config.apiUrl}
            onChange={(_, data) => onConfigChange('apiUrl', data.value)}
            placeholder={domainFeedPlaceholder}
            contentBefore={<Link24Regular data-testid="domain-feed-settings-link24-regular" />} data-testid="domain-feed-settings-api-url-input" />

        </Field>
      </div>

      <div className={mergeClasses(styles.fieldGroup, styles.fieldGroupContent)} data-testid="domain-feed-settings-field-group-2-div">
        <div className={styles.fieldGroupSubContent} data-testid="domain-feed-settings-field-group-sub-content-div">
          <Text data-testid="domain-feed-settings-token-expiry-date-text">Token expiry date</Text>
          <TokenExpiryText apiKeyExpirationDate={apiKeyExpirationDate} styles={styles} />
        </div>
        <Field
          label="API Key"
          hint="This key is provided by your domain feed provider."
          required data-testid="domain-feed-settings-api-key-field">

          <Input
            type={showApiKey ? 'text' : 'password'}
            value={config.apiKey}
            onChange={(_, data) => onConfigChange('apiKey', data.value)}
            placeholder="Enter your Domain Feed API key"
            contentBefore={<Key24Regular data-testid="domain-feed-settings-key24-regular" />}
            contentAfter={
            <div style={{ display: 'flex', gap: '4px' }} data-testid="domain-feed-settings-api-key-input-actions-div">
                <Button
                appearance="transparent"
                size="small"
                onClick={onToggleApiKeyVisibility} data-testid="domain-feed-settings-toggle-api-key-visibility-button">

                  {showApiKey ? 'Hide' : 'Show'}
                </Button>
                {config.apiKey &&
                <Button
                  appearance="transparent"
                  icon={<Copy24Regular data-testid="domain-feed-settings-copy24-regular" />}
                  size="small"
                  onClick={onCopyApiKey} data-testid="domain-feed-settings-copy-api-key-button" />

                }
              </div>
            } data-testid="domain-feed-settings-enter-your-domain-feed-api-key-input" />

        </Field>

        <div className={styles.apiKeyActions} data-testid="domain-feed-settings-api-key-actions-div">
          {shouldShowGenerateApiKeyDialog() ?
          <Dialog
            open={generateApiKeyDialog}
            onOpenChange={(_, data) => {
              if (!generatingApiKey) {
                onGenerateApiKeyDialogChange(data.open);
              }
            }} data-testid="domain-feed-settings-generate-api-key-dialog">
              <DialogTrigger disableButtonEnhancement data-testid="domain-feed-settings-open-generate-api-key-dialog-trigger">
                <Button appearance='primary' icon={<KeyReset24Regular data-testid="domain-feed-settings-generate-api-key-dialog-key-reset24-regular" />} data-testid="domain-feed-settings-generate-new-api-key-button">Generate new API key</Button>
              </DialogTrigger>
              <DialogSurface data-testid="domain-feed-settings-generate-api-key-dialog-surface">
                <DialogBody data-testid="domain-feed-settings-generate-api-key-dialog-body">
                  <DialogTitle action={
                generatingApiKey ? null : <DialogTrigger action="close" data-testid="domain-feed-settings-close-generate-api-key-dialog-trigger">
                      <Button
                    appearance="subtle"
                    aria-label="close"
                    icon={<Dismiss24Regular data-testid="domain-feed-settings-generate-api-key-close-dismiss24-regular" />} data-testid="domain-feed-settings-close-button" />

                    </DialogTrigger>
                } data-testid="domain-feed-settings-generate-new-api-key-dialog-title">Generate new API key</DialogTitle>
                  <DialogContent data-testid="domain-feed-settings-generate-api-key-dialog-content">
                    <p data-testid="domain-feed-settings-p">Generating a new API key will disable the current key. Do you want to continue?</p>
                  </DialogContent>
                  <DialogActions data-testid="domain-feed-settings-generate-api-key-dialog-actions">
                    <DialogTrigger disableButtonEnhancement data-testid="domain-feed-settings-cancel-generate-api-key-dialog-trigger">
                      <Button
                      appearance="secondary"
                      disabled={generatingApiKey} data-testid="domain-feed-settings-cancel-button">

                        Cancel
                      </Button>
                    </DialogTrigger>
                    <Button
                    appearance="primary"
                    onClick={onGenerateNewApiKey}
                    disabled={generatingApiKey} data-testid="domain-feed-settings-generate-api-key-confirm-button">

                      {generatingApiKey ? 'Generating...' : 'Generate'}
                    </Button>
                  </DialogActions>
                </DialogBody>

              </DialogSurface>
            </Dialog> :

          <Button appearance='primary' disabled={generatingApiKey} onClick={onGenerateNewApiKey} icon={<KeyReset24Regular data-testid="domain-feed-settings-generate-api-key-direct-key-reset24-regular" />} data-testid="domain-feed-settings-generate-api-key-direct-button">{generatingApiKey ? 'Generating new API key...' : 'Generate new API key'}</Button>
          }

        </div>

      </div>

      <div className={mergeClasses(styles.fieldGroup, styles.noMarginBottom)} data-testid="domain-feed-settings-field-group-3-div">
        <Field label="Enable Automatic Sync" data-testid="domain-feed-settings-enable-automatic-sync-field">
          <Switch
            checked={config.enabled}
            onChange={(_, data) => onConfigChange('enabled', data.checked)}
            label={config.enabled ? 'Automatic synchronization enabled' : 'Automatic synchronization disabled'}
            data-testid='enable-sync-switch' />

        </Field>
      </div>

      {config.enabled &&
      <div className={styles.fieldGroup}>
          {config.lastSyncStatus &&
          <div style={{
            marginTop: '12px',
            padding: '8px',
            backgroundColor: config.lastSyncStatus === 'success' ?
            tokens.colorPaletteGreenBackground1 :
            tokens.colorPaletteRedBackground1,
            borderRadius: '4px'
          }} data-testid="domain-feed-settings-last-sync-status-div"
          >
              <Text size={200} data-testid="domain-feed-settings-last-sync-status-text">
                Last sync: {config.lastSyncStatus === 'success' ? '✅' : '❌'} {config.lastSyncMessage || 'No details available'}
              </Text>
            </div>
          }
          {!!config.syncIntervalMinutes &&
          <div style={{ marginTop: '8px' }} data-testid="domain-feed-settings-next-sync-time-div">
              <Text size={200} data-testid="domain-feed-settings-next-sync-text">
                Next sync: {getNextSyncDisplay(config)}
              </Text>
            </div>
          }

          <div className={styles.fieldGroup} style={{ marginTop: '20px' }} data-testid="domain-feed-settings-field-group-5-div">
            <Field label="Auto-Approve New Domains" data-testid="domain-feed-settings-auto-approve-new-domains-field">
              <Switch
              checked={config.autoApproveDomains || false}
              onChange={(_, data) => onConfigChange('autoApproveDomains', data.checked)}
              label={config.autoApproveDomains ? 'Automatically approve new domains from feed' : 'Require manual approval for new domains'}
              data-testid='auto-approve-domains-switch' />

              {config.autoApproveDomains &&
              <Text style={{ fontSize: '12px', color: '#D13438', marginTop: '4px', fontWeight: '600', display: 'block' }} data-testid="domain-feed-settings-warning-new-domains-will-be-text">
                  ⚠️ Warning: New domains will be automatically approved without manual review
                </Text>
              }
              {!config.autoApproveDomains &&
              <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground2, marginTop: '4px', display: 'block' }} data-testid="domain-feed-settings-new-domains-from-the-feed-will-text">
                  New domains from the feed will require manual approval before use
                </Text>
              }
            </Field>
          </div>

          <div className={mergeClasses(styles.fieldGroup, styles.hiddenForPilot)} data-feature="allow-policy-updates" data-testid="domain-feed-settings-field-group-6-div">
            <Field label="Allow Policy Updates Automatically" data-testid="domain-feed-settings-allow-policy-updates-automatically-field">
              <Switch
              checked={config.allowPolicyUpdates || false}
              onChange={(_, data) => onConfigChange('allowPolicyUpdates', data.checked)}
              label={config.allowPolicyUpdates ? 'Automatically update policies when domains change in the feed' : 'Require manual action for policy updates'}
              data-testid='allow-policy-updates-switch' />

              {config.allowPolicyUpdates &&
              <Text style={{ fontSize: '12px', color: '#D13438', marginTop: '4px', fontWeight: '600', display: 'block' }} data-testid="domain-feed-settings-warning-policies-will-be-automatically-text">
                  ⚠️ Warning: Policies will be automatically updated when domains are added or removed from the feed
                </Text>
              }
              {!config.allowPolicyUpdates &&
              <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground2, marginTop: '4px', display: 'block' }} data-testid="domain-feed-settings-policy-updates-will-require-manual-approval-text">
                  Policy updates will require manual approval when domains change in the feed
                </Text>
              }
            </Field>
          </div>
        </div>
      }
    </Card>);

};

interface RestrictedDomainsCardProps {
  styles: DomainFeedSettingsStyles;
  restrictedDomains: string[];
  newDomain: string;
  isDomainDuplicate: boolean;
  isInvalidDomain: boolean;
  addDomainButtonTitle: string;
  onNewDomainChange: (value: string) => void;
  onAddDomain: () => void;
  onRemoveDomain: (domain: string) => void;
}

const RestrictedDomainsCard: React.FC<RestrictedDomainsCardProps> = ({
  styles,
  restrictedDomains,
  newDomain,
  isDomainDuplicate,
  isInvalidDomain,
  addDomainButtonTitle,
  onNewDomainChange,
  onAddDomain,
  onRemoveDomain
}) => (
  <Card data-testid="domain-feed-settings-restricted-domains-card">
    <CardHeader
      header={<Text weight="semibold" size={500} data-testid="domain-feed-settings-restricted-domains-list-text">Restricted domains list</Text>}
      description="Domains that will never be allowed in your organization" data-testid="domain-feed-settings-restricted-domains-card-header" />

    <div className={styles.restrictedDomainsSection} data-testid="domain-feed-settings-restricted-domains-section-div">
      <Text className={styles.warningText} data-testid="domain-feed-settings-if-there-are-any-domains-that-text">
        If there are any domains that must never be available in your organization, you can add them here. You can change this list in the future.
      </Text>

      <div className={styles.domainsList} data-testid="domain-feed-settings-domains-list-div">
        {restrictedDomains.map((domain) =>
        <div key={domain} className={styles.domainItem} data-testid="domain-feed-settings-domain-item-div">
            <Text data-testid="domain-feed-settings-restricted-domain-text">{domain}</Text>
            <Button
            appearance="subtle"
            icon={<Delete20Regular data-testid="domain-feed-settings-delete20-regular" />}
            className={styles.removeButton}
            onClick={() => onRemoveDomain(domain)}
            aria-label={`Remove ${domain}`} data-testid="domain-feed-settings-remove-restricted-domain-button" />

          </div>
        )}
      </div>

      <div className={styles.domainInput} data-testid="domain-feed-settings-domain-input-div">
        <Input
          placeholder="Enter domain (e.g., example.com)"
          value={newDomain}
          onChange={(_, data) => onNewDomainChange(data.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isDomainDuplicate && !isInvalidDomain) {
              onAddDomain();
            }
          }}
          style={{ flex: 1 }} data-testid="domain-feed-settings-enter-domain-e-g-example-com-input" />

        <Button
          appearance="secondary"
          icon={<Add20Regular data-testid="domain-feed-settings-add20-regular" />}
          onClick={onAddDomain}
          disabled={!newDomain.trim() || isDomainDuplicate || isInvalidDomain}
          title={addDomainButtonTitle} data-testid="domain-feed-settings-add-domain-button">

          Add domain
        </Button>
      </div>
      {isDomainDuplicate &&
      <Text style={{ fontSize: '12px', color: '#D13438', marginTop: '4px' }} data-testid="domain-feed-settings-this-domain-is-already-in-the-text">
          This domain is already in the restricted list
        </Text>
      }
      {isInvalidDomain &&
      <Text style={{ fontSize: '12px', color: '#D13438', marginTop: '4px' }} data-testid="domain-feed-settings-invalid-domain-format-please-enter-a-text">
          Invalid domain format. Please enter a valid domain (e.g., example.com)
        </Text>
      }
    </div>
  </Card>
);

const processEmailNotifications = (lsegData: ConfigResponse): DomainFeedConfig['emailNotifications'] => {
  const emailNotifications: DomainFeedConfig['emailNotifications'] = lsegData.emailNotifications || {
    enabled: false,
    recipientEmail: '',
    recipientEmails: [],
    notifyOnNewDomains: false,
    notifyOnRemovedDomains: false,
    notifyOnApprovalRequired: true
  };

  if (!emailNotifications.recipientEmails && emailNotifications.recipientEmail) {
    emailNotifications.recipientEmails = [emailNotifications.recipientEmail];
  }

  return emailNotifications;
};

const buildConfigObject = (lsegData: ConfigResponse, syncData: any, appConfig: any, emailNotifications: DomainFeedConfig['emailNotifications']): DomainFeedConfig => {
  return {
    apiUrl: lsegData.apiUrl ?? getDefaultDomainFeedUrl(),
    apiKey: lsegData.apiKey || '',
    enabled: syncData.enabled || false,
    syncIntervalMinutes: syncData.intervalMinutes || 360,
    lastSync: syncData.lastSync,
    nextSync: syncData.nextSync,
    lastSyncStatus: syncData.lastSyncStatus,
    lastSyncMessage: syncData.lastSyncMessage,
    totalDomains: lsegData.totalDomains,
    totalDomainsSynced: syncData.totalDomainsSynced,
    approvedDomains: lsegData.approvedDomains,
    pendingDomains: lsegData.pendingDomains,
    emailNotifications,
    autoApproveDomains: appConfig?.autoApproveDomains || false,
    allowPolicyUpdates: appConfig?.allowPolicyUpdates || false,
    restrictedDomains: appConfig?.restrictedDomains || []
  };
};

const DomainFeedSettingsContent: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { refreshData } = useUnifiedApp(); // Get refresh function to update header status
  const eventTracker = useEventTracker();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [config, setConfig] = useState<DomainFeedConfig>({
    apiUrl: getDefaultDomainFeedUrl(),
    apiKey: '',
    enabled: false,
    syncIntervalMinutes: 360
  });
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    connected: false
  });
  const [message, setMessage] = useState<SettingsMessage | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [restrictedDomains, setRestrictedDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [showSaveWarning, setShowSaveWarning] = useState(false);
  const [generatingApiKey, setGeneratingApiKey] = useState(false);
  const [generateApiKeyDialog, setGenerateApiKeyDialog] = useState(false);

  const apiKeyExpirationDate = getApiKeyExpirationDate(config.apiKey);

  // Check if the entered domain already exists (case-insensitive)
  const isDomainDuplicate = Boolean(newDomain.trim() && restrictedDomains.some((d) => d.toLowerCase() === newDomain.trim().toLowerCase()));

  // Check if domain format is invalid
  const isInvalidDomain = Boolean(newDomain.trim() && !isValidDomainFormat(newDomain));
  const addDomainButtonTitle = getAddDomainButtonTitle(isDomainDuplicate, isInvalidDomain);

  useEffect(() => {
    loadConfiguration();
    loadNotifications();
  }, []);

  const loadLsegConfiguration = async (): Promise<ConfigResponse | null> => {
    const lsegConfigResponse = await apiClient.get<ConfigResponse>('/lseg/config');
    return lsegConfigResponse?.data || lsegConfigResponse;
  };

  const loadSyncConfiguration = async (): Promise<any> => {
    const syncConfigResponse = await apiClient.get<any>('/lseg/sync-scheduler/config');
    return syncConfigResponse?.data?.data || syncConfigResponse?.data || {};
  };

  const loadAppConfiguration = async (): Promise<any> => {
    const appConfigResponse = await apiClient.get<any>('/config');
    return appConfigResponse?.data || appConfigResponse;
  };

  const testInitialConnection = async (lsegData: ConfigResponse): Promise<void> => {
    if (lsegData.apiKey) {
      await testConnection(lsegData.apiUrl, lsegData.apiKey, false);
    }
  };

  const loadConfiguration = async () => {
    try {
      setLoading(true);

      // Load all configurations
      const lsegData = await loadLsegConfiguration();
      const syncData = await loadSyncConfiguration();
      const appConfig = await loadAppConfiguration();

      if (lsegData) {
        // Process email notifications
        const emailNotifications = processEmailNotifications(lsegData);

        // Build and set configuration object
        const configObject = buildConfigObject(lsegData, syncData, appConfig, emailNotifications);
        setConfig(configObject);

        // Load restricted domains
        setRestrictedDomains(configObject.restrictedDomains || []);

        // Test connection on page load if API key exists (to show current status)
        await testInitialConnection(lsegData);
      }
    } catch (error) {
      console.error('Failed to load configuration:', error);
      setMessage({ type: 'error', text: 'Failed to load domain feed configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (key: keyof DomainFeedConfig, value: any) => {
    setConfig((prev) => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
    setMessage(null);
  };

  const handleAddDomain = () => {
    if (newDomain && !restrictedDomains.includes(newDomain)) {
      setRestrictedDomains([...restrictedDomains, newDomain]);
      setNewDomain('');
      setHasChanges(true);
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setRestrictedDomains(restrictedDomains.filter((d) => d !== domain));
    setHasChanges(true);
  };

  const validateConnectionParams = (testUrl: string, testKey: string): boolean => {
    if (!testUrl || !testKey) {
      setMessage({ type: 'error', text: 'API URL and API Key are required' });
      setConnectionStatus({ connected: false, error: 'Missing configuration' });
      return false;
    }
    return true;
  };

  const performConnectionTest = async (testUrl: string, testKey: string): Promise<TestConnectionResponse> => {
    const response = await apiClient.post<TestConnectionResponse>('/lseg/test-connection', {
      apiKey: testKey
    });
    return response?.data || response;
  };

  const handleSuccessfulConnection = (data: TestConnectionResponse, showMessage: boolean): void => {
    setConnectionStatus({
      connected: true,
      lastCheck: new Date().toISOString(),
      domainCount: data.domainCount,
      lastUpdated: data.lastUpdated
    });

    if (showMessage) {
      setMessage({
        type: 'success',
        text: `Connection successful! Found ${data.domainCount} banking domains in the feed.`
      });
    }
  };

  const handleFailedConnection = (data: TestConnectionResponse, showMessage: boolean): void => {
    setConnectionStatus({
      connected: false,
      error: data?.error || 'Connection failed',
      lastCheck: new Date().toISOString()
    });

    if (showMessage) {
      setMessage({
        type: 'error',
        text: data?.error || 'Failed to connect to domain feed'
      });
    }
  };

  const handleConnectionError = (error: any, showMessage: boolean): void => {
    console.error('Connection test failed:', error);
    const errorMessage = error?.details?.message || error?.message || 'Connection failed';
    
    setConnectionStatus({
      connected: false,
      error: errorMessage,
      lastCheck: new Date().toISOString()
    });

    if (showMessage) {
      setMessage({
        type: 'error',
        text: `Connection test failed: ${errorMessage}`
      });
    }
  };

  const testConnection = async (url?: string, key?: string, showMessage = true) => {
    try {
      setTesting(true);
      if (showMessage) {
        setMessage({ type: 'info', text: 'Testing connection to Banking Domain Feed...' });
      }

      const testUrl = url || config.apiUrl;
      const testKey = key || config.apiKey;

      if (!validateConnectionParams(testUrl, testKey)) {
        return;
      }

      // Test the connection using the proxy endpoint
      const data = await performConnectionTest(testUrl, testKey);

      if (data?.success) {
        handleSuccessfulConnection(data, showMessage);
      } else {
        handleFailedConnection(data, showMessage);
      }
    } catch (error: any) {
      handleConnectionError(error, showMessage);
    } finally {
      setTesting(false);
      // Refresh header status indicators to reflect the connection test result
      refreshData(true); // silent=true to avoid showing loading state
    }
  };

  const handleSave = async () => {
    // Check if connection test has failed and show warning
    if (!connectionStatus.connected && connectionStatus.lastCheck) {
      setShowSaveWarning(true);
      return;
    }

    await saveConfiguration();
  };

  const updateLsegApiKeyUrlConfiguration = async (extraParams?: {[key: string]: any;}) => {
    // Save LSEG configuration
    const lsegResponse = await apiClient.post<{success?: boolean;}>('/lseg/config', {
      apiUrl: config.apiUrl,
      apiKey: config.apiKey,
      ...extraParams
    });
    return lsegResponse;
  };

  const generateNewApiKey = async (invalidateKey: boolean = true) => {
    try {
      setGeneratingApiKey(true);
      setMessage({ type: 'info', text: 'Generating new API key...' });
      eventTracker.eventTrackOnElementClicked({
        pageName: PageName.SETTINGS_DOMAIN_FEED,
        elementName: ElementButton.GENERATE_NEW_API_KEY,
        elementType: 'button',
      }).catch(() => {});
      const key = await apiClient.post<{apiKey: string;}>('api-key/rotate', {
        oldKey: invalidateKey ? config.apiKey : undefined
      });
      config.apiKey = key.data.apiKey;
      setGeneratingApiKey(false);
      setGenerateApiKeyDialog(false);
      setMessage({ type: 'success', text: 'New API key created. Your new API key is ready to use.' });
    } catch (error) {
      console.error('Failed to generate new API key:', error);
      // If error is 403, try generating a new token without invalidation. It is possible that the user is using an incorrect key
      if (error instanceof ServiceError && error.statusCode === 403 && invalidateKey) {
        await generateNewApiKey(false);
      } else {
        const errorMessage = error instanceof ServiceError ? error.message : 'Failed to generate new API key';
        setMessage({ type: 'error', text: errorMessage });
      }
      setGenerateApiKeyDialog(false);
      setGeneratingApiKey(false);
    }
  };

  const generateApiKeyShouldShowDialog = (): boolean => {
    // If key already expired then do not show dialog, else show
    if (apiKeyExpirationDate) {
      const now = new Date();
      const expiration = new Date(apiKeyExpirationDate);
      return expiration > now;
    }
    return true; // Show dialog if no expiration date is set
  };

  const saveConfiguration = async () => {
    try {
      setSaving(true);
      setMessage(null);
      setShowSaveWarning(false); // Close warning dialog if open

      if (!config.apiUrl || !config.apiKey) {
        setMessage({ type: 'error', text: 'API URL and API Key are required' });
        return;
      }

      eventTracker.eventTrackOnElementClicked({
        pageName: PageName.SETTINGS_DOMAIN_FEED,
        elementName: ElementButton.SAVE_CONFIGURATION,
        elementType: 'button',
        automaticDomainFeedSync: config.enabled ? 'ON' : 'OFF',
        ...(config.enabled && { autoApproveNewDomains: config.autoApproveDomains ? 'ON' : 'OFF' }),
        restrictedDomains: restrictedDomains,
      }).catch(() => {});

      const lsegResponse = await updateLsegApiKeyUrlConfiguration({
        emailNotifications: config.emailNotifications
      });

      // Update sync scheduler configuration
      const syncResponse = await apiClient.post<{success?: boolean;}>('/lseg/sync-scheduler/config', {
        enabled: config.enabled,
        intervalMinutes: config.syncIntervalMinutes
      });

      // Save domain feed settings (auto-approve and auto-update toggles)
      const appConfigResponse = await apiClient.post<{success?: boolean;}>('/config', {
        autoApproveDomains: config.autoApproveDomains || false,
        allowPolicyUpdates: config.allowPolicyUpdates || false,
        restrictedDomains: restrictedDomains
      });

      if (lsegResponse?.success && syncResponse?.success && appConfigResponse?.success) {
        setMessage({ type: 'success', text: 'Domain feed configuration saved successfully' });
        setHasChanges(false);

        // Test the connection with the new configuration to update the page status badge
        await testConnection(config.apiUrl, config.apiKey, false); // Don't show message, we already have success message

        // Refresh header status indicators to reflect the new configuration
        refreshData(true); // silent=true to avoid showing loading state

        // If enabled, trigger immediate sync
        if (config.enabled) {
          await syncDomains();
        }
      } else {
        setMessage({ type: 'error', text: 'Failed to save configuration' });
      }
    } catch (error: any) {
      console.error('Failed to save configuration:', error);
      setMessage({ type: 'error', text: `Failed to save: ${error.message}` });
      // Test connection to update page status (will show disconnected)
      await testConnection(config.apiUrl, config.apiKey, false);
      // Refresh to show disconnected status after save failure
      refreshData(true);
    } finally {
      setSaving(false);
    }
  };

  const syncDomains = async () => {
    try {
      setSyncing(true);
      setMessage({ type: 'info', text: 'Synchronizing domains from LSEG feed...' });

      eventTracker.eventTrackOnElementClicked({
        pageName: PageName.SETTINGS_DOMAIN_FEED,
        elementName: ElementButton.SYNC_DOMAINS_NOW,
        elementType: 'button',
      }).catch(() => {});

      // Use the new sync scheduler trigger endpoint
      const response = await apiClient.post<any>('/lseg/sync-scheduler/trigger');

      if (response?.success) {
        const data = response.data || response;
        const changes = data.changes;
        let messageText = data.message || `Successfully synced ${data.domainsCount} domains`;

        if (changes && (changes.newDomains?.length > 0 || changes.removedDomains?.length > 0)) {
          messageText += ` (${changes.newDomains?.length || 0} new, ${changes.removedDomains?.length || 0} removed)`;
        }

        setMessage({
          type: 'success',
          text: messageText
        });

        // Reload configuration and notifications to get updated sync status
        await loadConfiguration();
        await loadNotifications();
      } else {
        const errorMessage = (response as any)?.error || 'Failed to synchronize domains';
        setMessage({ type: 'error', text: errorMessage });
      }
    } catch (error: any) {
      console.error('Failed to sync domains:', error);
      setMessage({ type: 'error', text: `Sync failed: ${error.message}` });
    } finally {
      setSyncing(false);
    }
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(config.apiKey);
    setMessage({ type: 'success', text: 'API key copied to clipboard' });
    setTimeout(() => setMessage(null), 3000);
  };

  const loadNotifications = async () => {
    try {
      const response = await apiClient.get<any>('/lseg/notifications');
      if (response?.success && response?.data) {
        setNotifications(response.data);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const acknowledgeNotification = async (notificationId: number) => {
    try {
      const response = await apiClient.post(`/lseg/notifications/${notificationId}/acknowledge`);
      if (response?.success) {
        // Reload notifications
        await loadNotifications();
        setMessage({ type: 'success', text: 'Notification acknowledged' });
      }
    } catch (error) {
      console.error('Failed to acknowledge notification:', error);
      setMessage({ type: 'error', text: 'Failed to acknowledge notification' });
    }
  };

  if (loading) {
    return (
      <div className={styles.container} data-testid="domain-feed-settings-container-div">
        <Spinner size="large" label="Loading domain feed settings..." data-testid="domain-feed-settings-loading-domain-feed-settings-spinner" />
      </div>);

  }

  return (
    <div className={styles.container} data-testid="domain-feed-settings-container-2-div">
      <div className={styles.header} data-testid="domain-feed-settings-header-div">
        <div data-testid="domain-feed-settings-header-content-div">
          <div className={styles.title} data-testid="domain-feed-settings-title-div">
            <CloudSync24Regular data-testid="domain-feed-settings-cloud-sync24-regular" />
            <Text size={600} weight="semibold" data-testid="domain-feed-settings-domain-feed-configuration-text">Domain Feed Configuration</Text>
          </div>
          <Text className={styles.subtitle} data-testid="domain-feed-settings-configure-the-external-domain-feed-api-text">
            Configure the external domain feed API for automated domain synchronization
          </Text>
        </div>
        <Button
          appearance="secondary"
          icon={<ArrowLeftRegular data-testid="domain-feed-settings-arrow-left-regular" />}
          onClick={() => navigate('/settings')} data-testid="domain-feed-settings-back-to-settings-button">

          Back to Settings
        </Button>
      </div>

      <MessageBanner message={message} onDismiss={() => setMessage(null)} />

      <NotificationsCard
        styles={styles}
        notifications={notifications}
        onAcknowledge={acknowledgeNotification} />

      <ConnectionStatusCard
        styles={styles}
        connectionStatus={connectionStatus}
        config={config}
        syncing={syncing}
        onSyncDomains={syncDomains} />

      <div className={styles.grid} data-testid="domain-feed-settings-grid-div">
        <ApiConfigurationCard
          styles={styles}
          config={config}
          showApiKey={showApiKey}
          apiKeyExpirationDate={apiKeyExpirationDate}
          generatingApiKey={generatingApiKey}
          generateApiKeyDialog={generateApiKeyDialog}
          onConfigChange={handleConfigChange}
          onToggleApiKeyVisibility={() => setShowApiKey(!showApiKey)}
          onCopyApiKey={handleCopyApiKey}
          onGenerateApiKeyDialogChange={setGenerateApiKeyDialog}
          onGenerateNewApiKey={() => generateNewApiKey()}
          shouldShowGenerateApiKeyDialog={generateApiKeyShouldShowDialog} />

        <RestrictedDomainsCard
          styles={styles}
          restrictedDomains={restrictedDomains}
          newDomain={newDomain}
          isDomainDuplicate={isDomainDuplicate}
          isInvalidDomain={isInvalidDomain}
          addDomainButtonTitle={addDomainButtonTitle}
          onNewDomainChange={setNewDomain}
          onAddDomain={handleAddDomain}
          onRemoveDomain={handleRemoveDomain} />
      </div>

      {/* Actions */}
      <div className={styles.actions} data-testid="domain-feed-settings-actions-div">
        <Button
          icon={<ArrowClockwise24Regular data-testid="domain-feed-settings-arrow-clockwise24-regular" />}
          onClick={() => {
            eventTracker.eventTrackOnElementClicked({
              pageName: PageName.SETTINGS_DOMAIN_FEED,
              elementName: ElementButton.TEST_CONNECTION,
              elementType: 'button',
            }).catch(() => {});
            testConnection();
          }}
          disabled={testing || !config.apiUrl || !config.apiKey}
          data-testid="test-connection-button">

          {testing ? 'Testing...' : 'Test Connection'}
        </Button>
        <Button
          appearance="primary"
          icon={<Save24Regular data-testid="domain-feed-settings-save24-regular" />}
          onClick={handleSave}
          disabled={!hasChanges || saving || !config.apiUrl || !config.apiKey || generatingApiKey} data-testid="domain-feed-settings-save-configuration-button">

          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
        <Button
          appearance="secondary"
          onClick={loadConfiguration}
          disabled={loading} data-testid="domain-feed-settings-reset-changes-button">

          Reset Changes
        </Button>
      </div>

      {hasChanges &&
      <div style={{ marginTop: '12px', padding: '8px', backgroundColor: tokens.colorPaletteYellowBackground1, borderRadius: '4px' }} data-testid="domain-feed-settings-unsaved-changes-warning-div">
          <Text size={200} data-testid="domain-feed-settings-you-have-unsaved-changes-click-text">
            * You have unsaved changes. Click Save Configuration to apply them.
          </Text>
        </div>
      }

      {/* Warning Dialog for Saving Bad Configuration */}
      <Dialog open={showSaveWarning} onOpenChange={(_, data) => setShowSaveWarning(data.open)} data-testid="domain-feed-settings-save-warning-dialog">
        <DialogSurface data-testid="domain-feed-settings-save-warning-dialog-surface">
          <DialogBody data-testid="domain-feed-settings-save-warning-dialog-body">
            <DialogTitle data-testid="domain-feed-settings-connection-test-failed-dialog-title">Connection Test Failed</DialogTitle>
            <DialogContent data-testid="domain-feed-settings-save-warning-dialog-content">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="domain-feed-settings-save-warning-content-div">
                <Text data-testid="domain-feed-settings-the-connection-test-failed-or-has-text">
                  The connection test failed or has not been run. Are you sure you want to save this configuration?
                </Text>
                {connectionStatus.error &&
                <div style={{
                  padding: '12px',
                  backgroundColor: tokens.colorPaletteRedBackground1,
                  borderRadius: '4px',
                  border: `1px solid ${tokens.colorPaletteRedBorder1}`
                }} data-testid="domain-feed-settings-save-warning-error-box-div">
                    <Text size={200} weight="semibold" style={{ color: tokens.colorPaletteRedForeground1 }} data-testid="domain-feed-settings-error-text">
                      Error: {connectionStatus.error}
                    </Text>
                  </div>
                }
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }} data-testid="domain-feed-settings-note-you-can-save-anyway-if-text">
                  Note: You can save anyway if you're configuring for a different environment or if the feed is temporarily unavailable.
                </Text>
              </div>
            </DialogContent>
            <DialogActions data-testid="domain-feed-settings-save-warning-dialog-actions">
              <Button appearance="secondary" onClick={() => setShowSaveWarning(false)} data-testid="cancel-save-warning-button">
                Cancel
              </Button>
              <Button appearance="primary" onClick={saveConfiguration} data-testid="domain-feed-settings-save-anyway-button">
                Save Anyway
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>);

};

export const DomainFeedSettings: React.FC = () => <DomainFeedSettingsContent />;

export default DomainFeedSettings;